/**
 * venue_client.ts — the CLIENT's reading of the venue bindings (the interiors programme, slice E, 2026-10-01). No
 * three, no DOM, no rows: the venue module, the Town window, the maps and the gate read these.
 *
 *   THE TABLE   `shared/venue_bindings.generated.ts` decoded once (the server reads its byte-identical twin).
 *   THE LEVER   `platform_flag interior_venues_v1` as this client last read it (`setVenuesEnabled`; ABSENT = ON).
 *               Off → every accessor here answers "no binding": the post is at its slot, a building keeps its civ
 *               name and purpose, the map names it by its function — today's game.
 *   THE NAME    a tavern reads the canonical venue name in its BUILDERS' voice (`style.md` §6.2: The Radiant Cup /
 *               The Frosthearth — the pack the building was built from, the same rule the sign inside its door uses);
 *               a clinic has no canonical name and reads "clinic".
 *   THE COUNTER what the venue module publishes each frame for whoever asks (the Town module's serve prompt; the
 *               presentation of staff and customers): the anchors of the bound room the player stands in.
 * globalThis-backed state (the instance-split law).
 */
import { VENUE_TABLE } from '../../../shared/venue_bindings.generated.js';
import {
  decodeVenues, venueAtSlot, venueOfBuilding, venueSiteOfId, venuePackCiv, venueDefOfPost, venueDef, type VenueBinding,
} from './venue_binding.js';
import { venueName } from './interior_style.js';
import type { MapCategory } from './map_structures.js';
import type { VenueAnchors } from './venue_anchor.js';

// ─── The table ───────────────────────────────────────────────────────────────────────────────────────────────────────

let list: VenueBinding[] | null = null;
let bySite: Map<number, VenueBinding[]> | null = null;
function load(): VenueBinding[] {
  if (!list) {
    list = decodeVenues(VENUE_TABLE);
    bySite = new Map();
    for (const b of list) { const l = bySite.get(b.site); if (l) l.push(b); else bySite.set(b.site, [b]); }
  }
  return list;
}
/** Every baked binding of the world with this seed (none for any other — a personal world has no town). */
export function venueBindings(seed: string | null | undefined): readonly VenueBinding[] {
  return seed === VENUE_TABLE.seed ? load() : [];
}
/** A site's bindings (at most one per venue kind). */
export function venuesOfSite(seed: string | null | undefined, site: number): readonly VenueBinding[] {
  if (seed !== VENUE_TABLE.seed) return [];
  load();
  return bySite!.get(site) ?? [];
}

// ─── The lever ───────────────────────────────────────────────────────────────────────────────────────────────────────

/** What a player's presentation of one versona is while they stand in a bound venue (world space). */
export interface VenueSpot { x: number; y: number; z: number; yaw: number; lying: boolean; role: 'staff' | 'customer' | 'queue' }
export interface VenueCounter {
  /** The enterable the player stands in, and the binding it is. */
  id: string; binding: VenueBinding;
  anchors: VenueAnchors;
  /** Distance from the player to the nearest staff / customer standing point (Infinity off the ground floor). */
  dStaff: number; dCustomer: number;
  /** A room with no counter: the post is served from anywhere inside. */
  anywhere: boolean;
  /** Who is presented where (versona id → spot), for the life layer. */
  spots: Map<string, VenueSpot>;
}
interface Store { on: boolean; counter: VenueCounter | null }
const G = globalThis as unknown as { __atlasVenues?: Store };
const store = (): Store => (G.__atlasVenues ??= { on: true, counter: null });

/** A `platform_flag` row (or its absence) → enabled. ABSENT = ON. */
export function venueFlagFrom(row: { enabled: boolean } | null | undefined): boolean { return row ? row.enabled !== false : true; }
export function venuesEnabled(): boolean { return store().on; }
export function setVenuesEnabled(on: boolean): void { store().on = on; if (!on) store().counter = null; }
/** The bound room the player stands in, as the venue module last published it (null outdoors, off, or unbound). */
export function venueCounter(): VenueCounter | null { return store().counter; }
export function setVenueCounter(c: VenueCounter | null): void { store().counter = c; }

// ─── Lookups under the lever ─────────────────────────────────────────────────────────────────────────────────────────

/** THE EFFECTIVE VENUE of a post row: the bound building its slot names (null = the slot — unbound, or the lever off). */
export function venueOfPostRow(seed: string | null | undefined, post: { postKey: string; venueX: number; venueZ: number }): VenueBinding | null {
  if (!venuesEnabled()) return null;
  const def = venueDefOfPost(post.postKey);
  return def ? venueAtSlot(venueBindings(seed), def.kind, post.venueX, post.venueZ) : null;
}
/** The binding of the slot a post WOULD be taken at (the Service posts tab's rows). */
export function venueOfSlot(seed: string | null | undefined, postKey: string, slotX: number, slotZ: number): VenueBinding | null {
  return venueOfPostRow(seed, { postKey, venueX: slotX, venueZ: slotZ });
}
/** The binding a registered building is (`settle:<site>:…` at the binding's seat, the same model), or null. */
export function venueOfEnterable(seed: string | null | undefined, e: { id: string; key: string; x?: number; z?: number }): VenueBinding | null {
  if (!venuesEnabled() || e.x === undefined || e.z === undefined) return null;
  const site = venueSiteOfId(e.id);
  if (site === null) return null;
  const mine = venuesOfSite(seed, site);
  return mine.length ? venueOfBuilding(mine, site, e.x, e.z, e.key) : null;
}

// ─── Names ───────────────────────────────────────────────────────────────────────────────────────────────────────────

/** What the venue is called: "The Radiant Cup" / "The Frosthearth" (the builders' pack); "the tavern" for a neutral
 *  builder; a clinic is "the clinic". */
export function venueTitle(b: Pick<VenueBinding, 'kind' | 'key'>): string {
  if (b.kind === 'tavern') return venueName('tavern', venuePackCiv(b.key)) ?? 'the tavern';
  return `the ${b.kind}`;
}
/** The registry name a bound building wears at its door (the prompt adds the purpose when the name does not say it). */
export function venueDoorName(b: Pick<VenueBinding, 'kind' | 'key'>): string {
  return b.kind === 'tavern' ? venueTitle(b) : b.kind;
}
/** "at the counter of The Radiant Cup" · "at the cots of the clinic" — where the post is, in words. */
export function venuePlaceText(b: Pick<VenueBinding, 'kind' | 'key'>): string {
  return b.kind === 'clinic' ? `at the cots of ${venueTitle(b)}` : `at the counter of ${venueTitle(b)}`;
}
/** The map's name, category and extra search words for the building at (site, index), or null (`setMapVenueNamer`). */
export function mapVenueLabel(seed: string | null | undefined, site: number, index: number): { name: string; category: MapCategory; detail: string } | null {
  if (!venuesEnabled()) return null;
  for (const b of venuesOfSite(seed, site)) {
    if (b.index !== index) continue;
    if (b.kind === 'tavern') return { name: venueName('tavern', venuePackCiv(b.key)) ?? 'Tavern', category: 'leisure', detail: 'tavern cantina publican counter venue' };
    return { name: 'Clinic', category: 'health', detail: 'clinic healer infirmary cots venue' };
  }
  return null;
}
/** The interior purpose a bound building resolves to. */
export function venuePurpose(b: Pick<VenueBinding, 'kind'>): 'hospitality' | 'clinic' {
  return venueDef(b.kind)?.purpose ?? 'hospitality';
}
